import * as SliderPrimitive from '@radix-ui/react-slider';
import { useTranslations } from 'next-intl';
import type { SubscriptionProductType } from '@/app/types';
import { cn } from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import type { UltimateProductOption } from '../utils';

export const UltimateProductSlider = ({
  options,
  selectedProduct,
  onChange,
  disabled,
}: {
  options: UltimateProductOption[];
  selectedProduct: SubscriptionProductType;
  onChange: (product: SubscriptionProductType) => void;
  disabled?: boolean;
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.product.id === selectedProduct.id),
  );
  const creditsLabel = (option: UltimateProductOption) => option.credits.toLocaleString(locale);

  const selectedOption = options[selectedIndex];
  /** 圆点在两端时的圆心位置：Radix 会把 Thumb 收在轨道内，圆心距两端各半个 Thumb（size-4 → 8px） */
  const tickLeft = (index: number) =>
    options.length > 1 ? `calc(8px + (100% - 16px) * ${index / (options.length - 1)})` : '50%';

  return (
    <div className="mb-2 px-5 pt-8">
      <div className="relative">
        {/* 未选中档位：圆心正上方的普通文字刻度；选中档位换成 Thumb 上的气泡 */}
        {options.map((option, index) => {
          const isSelected = index === selectedIndex;
          return (
            <button
              key={option.product.id}
              type="button"
              disabled={disabled}
              aria-pressed={isSelected}
              aria-label={`${creditsLabel(option)} ${t('credits')}`}
              className={cn(
                '-translate-x-1/2 absolute bottom-full mb-2 whitespace-nowrap rounded-4 py-0.5 text-system-white48 text-xs leading-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#9B93FF]',
                isSelected && 'invisible',
              )}
              style={{ left: tickLeft(index) }}
              onClick={() => onChange(option.product)}
            >
              {creditsLabel(option)}
            </button>
          );
        })}
        <SliderPrimitive.Root
          className="relative flex h-5 w-full touch-none select-none items-center [&>span]:transition-[left] [&>span]:duration-[450ms] [&>span]:ease-[cubic-bezier(0.34,1.56,0.64,1)]"
          min={0}
          max={options.length - 1}
          step={1}
          value={[selectedIndex]}
          disabled={disabled}
          onValueChange={([index]) => {
            const option = options[index];
            if (option) onChange(option.product);
          }}
        >
          {/* 轨道两端内缩半个 Thumb，使轴恰好从左侧数字中心延伸到右侧数字中心 */}
          <SliderPrimitive.Track className="relative mx-2 h-1 grow rounded-full bg-system-white12">
            <SliderPrimitive.Range className="absolute h-full rounded-full bg-[#9B93FF] transition-[left,right] duration-[450ms] ease-[cubic-bezier(0.34,1.56,0.64,1)]" />
          </SliderPrimitive.Track>
          <SliderPrimitive.Thumb
            aria-label={t('credits')}
            aria-valuetext={`${creditsLabel(selectedOption)} ${t('credits')}`}
            className="relative block size-4 rounded-full bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9B93FF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#161722]"
          >
            <span
              aria-hidden
              className="-translate-x-1/2 pointer-events-none absolute bottom-full left-1/2 mb-1.5 size-2 rotate-45 rounded-[1px] bg-[#9B93FF]"
            />
            <span
              key={selectedOption.product.id}
              aria-hidden
              className="-translate-x-1/2 fade-in-0 slide-in-from-bottom-1 pointer-events-none absolute bottom-full left-1/2 mb-2.5 animate-in whitespace-nowrap rounded-6 bg-[#9B93FF] px-2 py-0.5 font-medium text-system-white text-xs leading-5 duration-300"
            >
              {creditsLabel(selectedOption)}
            </span>
          </SliderPrimitive.Thumb>
        </SliderPrimitive.Root>
      </div>
    </div>
  );
};
